// src/components/shops/postTemplates/index.tsx
//
// Registry and renderer for marketing templates.
//
// Only templates that are actually implemented are registered. The `order`
// values are the contract with post_template_limit — a tier allowing N gets the
// first N — so they stay fixed as more templates land, and new ones are added
// at the end rather than inserted.
//
// The four families are the point of the set: a shop using three of these
// should look like it hired one designer, not three. That is what a studio
// sells, and it is why the list is thirteen considered looks rather than fifty.

import type { ReactElement } from 'react'
import QuietCard from './QuietCard'
import Editorial from './Editorial'
import MenuBoard from './MenuBoard'
import ContactSheet from './ContactSheet'
import Ticket from './Ticket'
import NowServing from './NowServing'
import Split from './Split'
import Marquee from './Marquee'
import FieldNote from './FieldNote'
import RecipeCard from './RecipeCard'
import Polaroid from './Polaroid'
import Stamp from './Stamp'
import Chalkboard from './Chalkboard'
import Seasonal from './Seasonal'
import Gift from './Gift'
import type { TemplateDef, TemplateId, TemplateProps } from './types'

export * from './types'

// Orders 1–2 are what Regular unlocks, 3–5 complete Staple, and the rest are
// Growth's. Within that, the order runs from the templates that flatter almost
// any post to the ones that need the right post to work.
export const TEMPLATES: TemplateDef[] = [
  { id: 'quiet', name: 'Quiet Card', purpose: 'Everyday updates', family: 'The People', order: 1 },
  { id: 'editorial', name: 'Editorial', purpose: 'Announcements', family: 'The Announcement', order: 2 },
  { id: 'menu-board', name: 'Menu Board', purpose: 'New drinks and specials', family: 'The Room', order: 3 },
  { id: 'contact-sheet', name: 'Contact Sheet', purpose: 'Events, several photos', family: 'The Craft', order: 4 },
  { id: 'ticket', name: 'Ticket', purpose: 'Offers and promotions', family: 'The Room', order: 5 },
  { id: 'now-serving', name: 'Now Serving', purpose: 'The weekly board', family: 'The Room', order: 6 },
  { id: 'split', name: 'Split', purpose: 'Short, confident statements', family: 'The Announcement', order: 7 },
  { id: 'marquee', name: 'Marquee', purpose: 'Closures and anniversaries', family: 'The Announcement', order: 8 },
  { id: 'field-note', name: 'Field Note', purpose: 'Single origin releases', family: 'The Craft', order: 9 },
  { id: 'recipe-card', name: 'Recipe Card', purpose: 'Brew guides', family: 'The Craft', order: 10 },
  { id: 'polaroid', name: 'Polaroid', purpose: 'Behind the counter', family: 'The People', order: 11 },
  { id: 'stamp', name: 'Stamp', purpose: 'Milestones', family: 'The People', order: 12 },
  { id: 'chalkboard', name: 'Chalkboard', purpose: 'The full menu, priced and described', family: 'The Room', order: 13 },
  { id: 'seasonal', name: 'Seasonal', purpose: 'Holiday hours & seasonal drinks', family: 'Seasonal', order: 14 },
  { id: 'gift', name: 'Gift', purpose: 'Gift cards & gifting', family: 'Seasonal', order: 15 },
]

const RENDERERS: Record<TemplateId, (p: TemplateProps) => ReactElement> = {
  quiet: QuietCard,
  editorial: Editorial,
  'menu-board': MenuBoard,
  'contact-sheet': ContactSheet,
  ticket: Ticket,
  'now-serving': NowServing,
  split: Split,
  marquee: Marquee,
  'field-note': FieldNote,
  'recipe-card': RecipeCard,
  polaroid: Polaroid,
  stamp: Stamp,
  chalkboard: Chalkboard,
  seasonal: Seasonal,
  gift: Gift,
}

export function isTemplateId(value: string | null | undefined): value is TemplateId {
  return !!value && TEMPLATES.some(t => t.id === value)
}

/**
 * Templates this shop may use. `limit` is post_template_limit from
 * get_shop_tier_features: -1 unlimited, 0 none, otherwise the first N.
 */
export function templatesForLimit(limit: number | null | undefined): TemplateDef[] {
  if (limit === -1) return TEMPLATES
  if (!limit || limit < 0) return []
  return TEMPLATES.filter(t => t.order <= limit)
}

/**
 * Renders a post in its chosen template. Returns null for an unknown or absent
 * id so callers can fall back to the plain card posts have always used — that
 * keeps every existing post rendering unchanged, and means a template removed
 * from the registry later degrades instead of throwing.
 */
export function PostTemplate({ template, ...data }: TemplateProps & { template?: string | null }) {
  if (!isTemplateId(template)) return null
  const Renderer = RENDERERS[template]
  return <Renderer {...data} />
}
